import assertStatus from "@mittwald/api-client/dist/types/assertStatus";
import invariant from "invariant";
import { mittwaldApi } from "../../api/Mittwald";
import Bytes from "../misc/Bytes";
import MoneyValue from "../misc/MoneyValue";
import type {
  Article,
  ArticleApiData,
  TariffChangeOrderPreviewInput,
} from "./";
import {
  ArticleFactory,
  ArticleTemplateName,
  HostingArticle,
  HostingPrices,
} from "./";

export interface SpaceServerArticleOrderInput {
  description: string;
  storageSizeInBytes: number;
  customerId: string;
  articleId: string;
  useFreeTrial?: boolean;
  promotionCode?: string;
  recommendationCode?: string;
}

export interface SpaceServerArticleOrderPreviewInput {
  storageSize: Bytes;
}

export class SpaceServerArticle extends HostingArticle {
  public static readonly templateName = ArticleTemplateName.spaceServerHosting;

  public constructor(data: ArticleApiData) {
    super(data);
  }

  public static coerce(article: Article): SpaceServerArticle {
    return new SpaceServerArticle(article.data);
  }

  public usePossibleArticleChanges(): SpaceServerArticle[] {
    if (this.data.possibleArticleChanges) {
      return this.data.possibleArticleChanges
        .map((c) => ArticleFactory.useLoadById(c.articleId))
        .map((a) => a.asType(SpaceServerArticle));
    }
    return [];
  }

  public useOrderPreview(
    input: SpaceServerArticleOrderPreviewInput,
  ): HostingPrices {
    const { storageSize } = input;

    const data = mittwaldApi.orderPreviewOrder
      .getResource({
        requestBody: {
          orderType: "server",
          orderData: {
            diskspaceInGiB: storageSize.in("GiB"),
            machineType: this.machineType.mustGetName(),
          },
        },
      })
      .useWatchData();

    invariant("storagePrice" in data, "Expected hosting order preview");

    return new HostingPrices(
      new MoneyValue(data.machineTypePrice),
      new MoneyValue(data.storagePrice),
    );
  }

  public useTariffChangeOrderPreview(
    input: TariffChangeOrderPreviewInput,
  ): HostingPrices {
    const { storageSize, contractId } = input;

    const data = mittwaldApi.orderPreviewTariffChange
      .getResource({
        requestBody: {
          tariffChangeType: "server",
          tariffChangeData: {
            contractId: contractId,
            diskspaceInGiB: storageSize.in("GiB"),
            machineType: this.machineType.name!,
          },
        },
      })
      .useWatchData();

    invariant("storagePrice" in data, "Expected hosting order preview");

    return new HostingPrices(
      new MoneyValue(data.machineTypePrice),
      new MoneyValue(data.storagePrice),
    );
  }

  public async getOrderPreview(
    input: SpaceServerArticleOrderPreviewInput,
  ): Promise<HostingPrices> {
    const { storageSize } = input;

    const response = await mittwaldApi.orderPreviewOrder.request({
      requestBody: {
        orderType: "server",
        orderData: {
          diskspaceInGiB: storageSize.in("GiB"),
          machineType: this.machineType.mustGetName(),
        },
      },
    });

    assertStatus(response, 200);

    invariant(
      "storagePrice" in response.content,
      "Expected hosting order preview",
    );

    return new HostingPrices(
      new MoneyValue(response.content.machineTypePrice),
      new MoneyValue(response.content.storagePrice),
    );
  }

  public async order(
    input: Omit<SpaceServerArticleOrderInput, "articleId">,
  ): Promise<string> {
    return SpaceServerArticle.order({ ...input, articleId: this.id });
  }

  public static async order(
    input: SpaceServerArticleOrderInput,
  ): Promise<string> {
    const {
      description,
      storageSizeInBytes,
      articleId,
      customerId,
      useFreeTrial,
      promotionCode,
      recommendationCode,
    } = input;

    const article = await SpaceServerArticle.getArticle(articleId);

    const machineTypeName = article.machineType.name;

    invariant(machineTypeName, "machine type name is required");

    const storageSize = Bytes.of(storageSizeInBytes, "bytes");

    const response = await mittwaldApi.orderCreateOrder.request({
      requestBody: {
        orderType: "server",
        orderData: {
          customerId,
          description,
          diskspaceInGiB: storageSize.in("GiB"),
          machineType: machineTypeName,
          useFreeTrial,
          promotionCode,
          recommendationCode,
        },
      },
    });

    assertStatus(response, 201);

    return response.content.orderId;
  }
}

export default SpaceServerArticle;
